import React,{useEffect,useState} from 'react';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {listReviewLinks} from '../../services/reviewLinkService';
import BusinessEditor from './BusinessEditor';

const {FiEdit2,FiImage,FiArrowUpRight,FiCopy,FiCheck}=FiIcons;

function BusinessCard({business,editing,onEdit,onSave,onCancel}) {
  const [copied,setCopied]=useState(false);
  const ownerPath=`/${business.slug}`;
  const reviewUrl=`${window.location.origin}${ownerPath}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(reviewUrl);
      setCopied(true);
      window.setTimeout(()=> setCopied(false),2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <article className="rounded-[1.5rem] border border-white/10 bg-[#19283d] p-5 transition-colors hover:border-[#3fc0fa]/40 sm:p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white">
          {business.image_url ? <img src={business.image_url} alt="" className="h-full w-full object-contain" /> : <SafeIcon icon={FiImage} className="text-2xl text-[#0b1423]" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold">{business.business_name}</h3>
          <p className="mt-1 text-sm text-slate-300">{business.address}</p>
          <Link to={ownerPath} className="mt-3 block break-all text-xs text-[#3fc0fa] hover:underline">
            {reviewUrl}
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={copyLink} className="site-outline inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold">
            <SafeIcon icon={copied ? FiCheck : FiCopy} /> {copied ? 'Copied' : 'Copy link'}
          </button>
          <Link to={ownerPath} className="site-outline inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold">
            Preview <SafeIcon icon={FiArrowUpRight} />
          </Link>
          <button type="button" onClick={onEdit} aria-expanded={editing} className="site-button inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold">
            <SafeIcon icon={FiEdit2} /> Edit
          </button>
        </div>
      </div>
      {editing && <BusinessEditor business={business} onSave={onSave} onCancel={onCancel} />}
    </article>
  );
}

export default function BusinessDirectory({refreshKey}) {
  const [businesses,setBusinesses]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [editing,setEditing]=useState(null);

  useEffect(()=> {
    let active=true;
    setLoading(true);
    setError('');
    listReviewLinks()
      .then((rows)=> {if (active) setBusinesses(rows);})
      .catch(()=> {if (active) setError('Could not load businesses. Please refresh and try again.');})
      .finally(()=> {if (active) setLoading(false);});
    return ()=> {active=false;};
  },[refreshKey]);

  function saved(record) {
    setBusinesses((rows)=> rows.map((row)=> row.id===record.id ? record : row));
    setEditing(null);
  }

  return (
    <section className="mt-16 border-t border-white/10 pt-12" aria-labelledby="business-directory-title">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="business-directory-title" className="text-3xl font-black sm:text-4xl">Businesses created</h2>
          <p className="mt-3 text-slate-300">Copy a link,preview a page,or edit its details.</p>
        </div>
        {!loading && !error && businesses.length > 0 && (
          <span className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-300">
            {businesses.length} {businesses.length===1 ? 'business' : 'businesses'}
          </span>
        )}
      </div>
      {loading && <p className="mt-8 text-slate-300">Loading businesses…</p>}
      {error && <p role="alert" className="mt-8 text-[#f8b86a]">{error}</p>}
      {!loading && !error && businesses.length===0 && (
        <p className="mt-8 rounded-2xl border border-dashed border-white/20 p-8 text-slate-300">
          No links yet. Search above to create one.
        </p>
      )}
      <div className="mt-8 grid gap-4">
        {businesses.map((business)=> (
          <BusinessCard
            key={business.id}
            business={business}
            editing={editing===business.id}
            onEdit={()=> setEditing(editing===business.id ? null : business.id)}
            onSave={saved}
            onCancel={()=> setEditing(null)}
          />
        ))}
      </div>
    </section>
  );
}